import { createFileRoute, Link } from '@tanstack/react-router';
import { Header, Footer } from '@/components/marketing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Circle, Clock, Sparkles, MessageSquare } from 'lucide-react';

export const Route = createFileRoute('/roadmap')({
  component: RoadmapPage,
});

type RoadmapStatus = 'shipped' | 'building' | 'planned' | 'considering';

interface RoadmapItem {
  title: string;
  description: string;
  status: RoadmapStatus;
}

const roadmapData = {
  shipped: [
    {
      title: 'Repository Analysis Engine',
      description: 'Deep analysis of codebases with support for 20+ programming languages.',
      status: 'shipped' as RoadmapStatus,
    },
    {
      title: 'PRD Generation',
      description: 'AI-powered product requirement document generation from code.',
      status: 'shipped' as RoadmapStatus,
    },
    {
      title: 'Feature Extraction',
      description: 'Automatically identify and catalog features from any codebase.',
      status: 'shipped' as RoadmapStatus,
    },
    {
      title: 'Security Scanning',
      description: 'Vulnerability detection and security best practice analysis.',
      status: 'shipped' as RoadmapStatus,
    },
    {
      title: 'Team Workspaces',
      description: 'Collaborative workspaces with role-based permissions.',
      status: 'shipped' as RoadmapStatus,
    },
    {
      title: 'Export Formats',
      description: 'Export PRDs and reports in Markdown, JSON, and PDF.',
      status: 'shipped' as RoadmapStatus,
    },
  ],
  building: [
    {
      title: 'VS Code Extension',
      description: 'Access ForgeNetwork analysis directly in your IDE.',
      status: 'building' as RoadmapStatus,
    },
    {
      title: 'Architecture Visualization',
      description: 'Interactive diagrams showing system architecture and dependencies.',
      status: 'building' as RoadmapStatus,
    },
    {
      title: 'Custom Analysis Rules',
      description: 'Define organization-specific patterns and analysis rules.',
      status: 'building' as RoadmapStatus,
    },
  ],
  planned: [
    {
      title: 'Jira & Linear Integration',
      description: 'Sync features and PRDs with project management tools.',
      status: 'planned' as RoadmapStatus,
    },
    {
      title: 'Notion Integration',
      description: 'Export and sync documentation directly to Notion.',
      status: 'planned' as RoadmapStatus,
    },
    {
      title: 'Real-time Collaboration',
      description: 'Live editing and commenting on PRDs with team members.',
      status: 'planned' as RoadmapStatus,
    },
    {
      title: 'API Changelog Detection',
      description: 'Track breaking changes across API versions automatically.',
      status: 'planned' as RoadmapStatus,
    },
  ],
  considering: [
    {
      title: 'Multi-Repository Analysis',
      description: 'Analyze dependencies and relationships across multiple repos.',
      status: 'considering' as RoadmapStatus,
    },
    {
      title: 'AI Refactoring Suggestions',
      description: 'Intelligent recommendations for code improvements.',
      status: 'considering' as RoadmapStatus,
    },
    {
      title: 'Compliance Reporting',
      description: 'SOC 2, HIPAA, and GDPR compliance analysis.',
      status: 'considering' as RoadmapStatus,
    },
    {
      title: 'GitHub Actions Integration',
      description: 'Run analysis automatically on pull requests.',
      status: 'considering' as RoadmapStatus,
    },
  ],
};

const statusConfig = {
  shipped: {
    icon: CheckCircle2,
    label: 'Shipped',
    color: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-100 dark:bg-green-900/30',
  },
  building: {
    icon: Clock,
    label: 'In Development',
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-100 dark:bg-blue-900/30',
  },
  planned: {
    icon: Circle,
    label: 'Planned',
    color: 'text-purple-600 dark:text-purple-400',
    bg: 'bg-purple-100 dark:bg-purple-900/30',
  },
  considering: {
    icon: Sparkles,
    label: 'Under Consideration',
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-100 dark:bg-amber-900/30',
  },
};

function RoadmapPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 pt-24 pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
          {/* Hero */}
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">Product</Badge>
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4">
              Product Roadmap
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              See what we've built, what we're working on, and what's coming next.
              This roadmap is shaped by customer feedback.
            </p>
          </div>

          {/* Roadmap Sections */}
          <div className="space-y-12">
            <RoadmapSection
              title="Recently Shipped"
              description="Features now available to all users"
              items={roadmapData.shipped}
              status="shipped"
            />
            <RoadmapSection
              title="Currently Building"
              description="Active development in progress"
              items={roadmapData.building}
              status="building"
            />
            <RoadmapSection
              title="Up Next"
              description="Scheduled for upcoming releases"
              items={roadmapData.planned}
              status="planned"
            />
            <RoadmapSection
              title="Under Consideration"
              description="Exploring based on customer interest"
              items={roadmapData.considering}
              status="considering"
            />
          </div>

          {/* Feedback CTA */}
          <section className="mt-16 bg-gradient-to-r from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12 text-center">
            <MessageSquare className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-4">Shape Our Roadmap</h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Have a feature request or feedback? We prioritize based on customer needs.
              Your input directly influences what we build.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg">
                <Link to="/contact" search={{ type: 'feedback' }}>Submit Feedback</Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/community">Join Community Discussions</Link>
              </Button>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function RoadmapSection({
  title,
  description,
  items,
  status,
}: {
  title: string;
  description: string;
  items: RoadmapItem[];
  status: RoadmapStatus;
}) {
  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <section>
      <div className="flex items-center gap-3 mb-2">
        <Icon className={`w-6 h-6 ${config.color}`} />
        <h2 className="text-2xl font-semibold">{title}</h2>
      </div>
      <p className="text-muted-foreground mb-6">{description}</p>
      <div className="grid gap-4 md:grid-cols-2">
        {items.map((item, index) => (
          <RoadmapCard key={index} item={item} />
        ))}
      </div>
    </section>
  );
}

function RoadmapCard({ item }: { item: RoadmapItem }) {
  const config = statusConfig[item.status];
  const Icon = config.icon;

  return (
    <div className="bg-card rounded-lg border p-5 flex items-start gap-4">
      <div className={`p-2 rounded-lg ${config.bg} shrink-0`}>
        <Icon className={`w-4 h-4 ${config.color}`} />
      </div>
      <div>
        <h3 className="font-semibold mb-1">{item.title}</h3>
        <p className="text-muted-foreground text-sm">{item.description}</p>
      </div>
    </div>
  );
}
